import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import { FiEye, FiEyeOff, FiPlus, FiStar, FiTrash2 } from "react-icons/fi";
import "./ContenidoAdmin.css";

/*
 * Gestor de contenido del entrenador: pega la URL de YouTube, elige tema y
 * listo. El cliente lo ve en su pestaña "Contenido".
 */

const CATEGORIAS = [
  ["titan", "De Titan Training"],
  ["entrenamiento", "Técnica y entrenamiento"],
  ["nutricion", "Nutrición"],
  ["salud", "Salud"],
  ["descanso", "Descanso y recuperación"],
];

const VACIO = {
  url: "",
  titulo: "",
  canal: "",
  duracion: "",
  categoria: "entrenamiento",
  porQue: "",
  destacado: false,
};

function ContenidoAdmin() {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState(null); // null = formulario cerrado
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { token } = await getSession();
      const res = await axios.get(`${API_URL}/contenido`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLista(res.data);
      setError("");
    } catch {
      setError("No se pudo cargar el contenido.");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const campo = (k) => ({
    value: form[k],
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
  });

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError("");
    try {
      const { token } = await getSession();
      await axios.post(`${API_URL}/contenido`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm(null);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  const alternarVisible = async (item) => {
    try {
      const { token } = await getSession();
      await axios.patch(
        `${API_URL}/contenido/${item._id}/visible`,
        { visible: !item.visible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      cargar();
    } catch {
      setError("No se pudo cambiar la visibilidad.");
    }
  };

  const borrar = async (item) => {
    try {
      const { token } = await getSession();
      await axios.delete(`${API_URL}/contenido/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      cargar();
    } catch {
      setError("No se pudo borrar.");
    }
  };

  const nombreCat = (c) => CATEGORIAS.find(([v]) => v === c)?.[1] || c;

  return (
    <div className="ca">
      {!form && (
        <button type="button" className="ca-nuevo" onClick={() => setForm({ ...VACIO })}>
          <FiPlus /> Añadir vídeo
        </button>
      )}

      {form && (
        <form className="ca-form" onSubmit={guardar}>
          <h3>Nuevo vídeo</h3>

          <label className="ca-campo ca-ancho">
            <span>Enlace de YouTube</span>
            <input
              type="url"
              required
              placeholder="https://www.youtube.com/watch?v=…"
              {...campo("url")}
            />
            <small>Vale cualquier formato: watch, youtu.be, shorts o embed.</small>
          </label>

          <label className="ca-campo ca-ancho">
            <span>Título</span>
            <input type="text" required maxLength={140} {...campo("titulo")} />
          </label>

          <label className="ca-campo">
            <span>Canal</span>
            <input type="text" maxLength={80} placeholder="Quién lo publica" {...campo("canal")} />
          </label>

          <label className="ca-campo">
            <span>Duración</span>
            <input type="text" maxLength={12} placeholder="12:40" {...campo("duracion")} />
          </label>

          <label className="ca-campo">
            <span>Tema</span>
            <select {...campo("categoria")}>
              {CATEGORIAS.map(([v, t]) => (
                <option key={v} value={v}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          <label className="ca-campo ca-check">
            <input
              type="checkbox"
              checked={form.destacado}
              onChange={(e) => setForm({ ...form, destacado: e.target.checked })}
            />
            <span>Destacar esta semana</span>
          </label>

          <label className="ca-campo ca-ancho">
            <span>Por qué se lo recomiendas</span>
            <textarea
              rows={2}
              maxLength={300}
              placeholder="Una línea que le diga al cliente qué va a sacar de aquí"
              {...campo("porQue")}
            />
          </label>

          {error && <p className="ca-error ca-ancho">{error}</p>}

          <div className="ca-form-botones ca-ancho">
            <button type="button" className="ca-btn-plano" onClick={() => setForm(null)}>
              Cancelar
            </button>
            <button type="submit" className="ca-btn" disabled={guardando}>
              {guardando ? "Guardando…" : "Añadir"}
            </button>
          </div>
        </form>
      )}

      {cargando && <div className="app-estado">Cargando…</div>}
      {!cargando && !lista.length && !form && (
        <p className="ca-vacio">
          Todavía no hay vídeos. Añade el primero y aparecerá en la app de tus clientes.
        </p>
      )}

      {error && !form && <p className="ca-error">{error}</p>}

      <ul className="ca-lista">
        {lista.map((item) => (
          <li key={item._id} className={item.visible ? "" : "ca-oculto"}>
            <img
              className="ca-mini"
              src={`https://i.ytimg.com/vi/${item.youtubeId}/default.jpg`}
              alt=""
              loading="lazy"
            />
            <div className="ca-txt">
              <strong>
                {item.destacado && <FiStar className="ca-estrella" aria-label="Destacado" />}
                {item.titulo}
              </strong>
              <span>
                {nombreCat(item.categoria)}
                {item.canal && ` · ${item.canal}`}
                {!item.visible && " · oculto"}
              </span>
            </div>
            <div className="ca-acciones">
              <button
                type="button"
                onClick={() => alternarVisible(item)}
                aria-label={item.visible ? "Ocultar" : "Mostrar"}
                title={item.visible ? "Ocultar a los clientes" : "Mostrar a los clientes"}
              >
                {item.visible ? <FiEye /> : <FiEyeOff />}
              </button>
              <button
                type="button"
                className="ca-borrar"
                onClick={() => borrar(item)}
                aria-label="Quitar"
                title="Quitar de la lista"
              >
                <FiTrash2 />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ContenidoAdmin;
